import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  MenuItem,
  CircularProgress,
  alpha,
  Zoom,
  Fade,
  useTheme
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import GroupIcon from '@mui/icons-material/Group';
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';

const CorporateBooking = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    designation: '',
    email: '',
    phone: '',
    eventType: '',
    numberOfAttendees: '',
    eventDate: '',
    venuePreference: '',
    budget: '',
    duration: '',
    requirements: ''
  });

  const steps = ['Company Details', 'Event Information', 'Budget & Needs'];

  const eventTypes = [
    'Corporate Retreat',
    'Team Building',
    'Conference',
    'Annual Meet',
    'Incentive Trip',
    'Product Launch',
    'Training Workshop',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateStep = (step) => {
    switch(step) {
      case 0:
        return formData.companyName && formData.contactPerson && formData.email && formData.phone;
      case 1:
        return formData.eventType && formData.numberOfAttendees && formData.eventDate;
      case 2:
        return formData.budget;
      default:
        return true;
    }
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      await API.post('/bookings', {
        userName: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        bookingDate: formData.eventDate,
        serviceType: `Corporate - ${formData.eventType}`,
        mealPreference: 'No Preference',
        numberOfPeople: parseInt(formData.numberOfAttendees),
        specialRequirements: `
          Company: ${formData.companyName}
          Designation: ${formData.designation}
          Venue Preference: ${formData.venuePreference}
          Budget: ${formData.budget}
          Duration: ${formData.duration}
          Additional Requirements: ${formData.requirements}
        `
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 4000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit corporate booking request');
    } finally {
      setLoading(false);
    }
  };

  const corporateFeatures = [
    {
      icon: <BusinessIcon sx={{ fontSize: 32 }} />,
      title: 'Customized Packages',
      description: 'Tailored corporate travel solutions designed exclusively to meet your business objectives.'
    },
    {
      icon: <GroupIcon sx={{ fontSize: 32 }} />,
      title: 'Group Management',
      description: 'Streamlined handling of large groups with dedicated on-ground coordinators.'
    },
    {
      icon: <EventIcon sx={{ fontSize: 32 }} />,
      title: 'Event Planning',
      description: 'End-to-end event management from venue sourcing to seamless logistics.'
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 32 }} />,
      title: 'Corporate Rates',
      description: 'Exclusive pricing models and flexible invoicing designed for business scale.'
    }
  ];

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: '#f8fafc', py: 8 }}>
        <Container maxWidth="sm">
          <Zoom in={true} timeout={600}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 6, 
                textAlign: 'center',
                borderRadius: 6,
                border: '1px solid rgba(0,0,0,0.05)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.04)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute', top: 0, left: 0, right: 0, height: 6, 
                  background: 'linear-gradient(90deg, #667eea, #764ba2)' 
                }} 
              />
              <Box sx={{ 
                width: 80, height: 80, 
                borderRadius: '50%', 
                bgcolor: alpha('#10b981', 0.1), 
                color: '#10b981',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 3
              }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#1a1a2e' }}>
                Request Submitted!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                Thank you, <strong>{formData.contactPerson}</strong>. We've received the details for <strong>{formData.companyName}</strong>. Our corporate concierge will reach out to you shortly.
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate('/')} 
                sx={{ 
                  px: 4, py: 1.2, borderRadius: 50, fontWeight: 700, textTransform: 'none',
                  background: 'linear-gradient(135deg, #0C2340 0%, #183B63 100%)',
                  boxShadow: '0 8px 20px rgba(12,35,64,0.3)',
                  '&:hover': {
                    boxShadow: '0 12px 25px rgba(12,35,64,0.4)',
                  }
                }}
              >
                Return to Home
              </Button>
            </Paper>
          </Zoom>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 10 }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url("https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            transform: 'scale(1.05)',
            transition: 'transform 8s ease-out',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(30,30,40,0.85) 0%, rgba(102,126,234,0.7) 100%)',
            zIndex: 1,
          },
          '&:hover::before': {
            transform: 'scale(1.1)',
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <Box sx={{ mb: 2, display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', px: 2, py: 0.8, borderRadius: 50, border: '1px solid rgba(255,255,255,0.2)' }}>
            <BusinessIcon sx={{ fontSize: 18, color: '#fcd34d' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
              Premium B2B Travel
            </Typography>
          </Box>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 800, textShadow: '0 4px 20px rgba(0,0,0,0.3)', mb: 2 }}>
            Corporate Services
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.9, maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}>
            Elevate your business retreats, conferences, and incentive trips with our world-class travel planning.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -5, position: 'relative', zIndex: 3 }}>
        {/* Features Grid */}
        <Grid container spacing={3} sx={{ mb: 10 }}>
          {corporateFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                elevation={0} 
                sx={{ 
                  height: '100%', 
                  textAlign: 'center',
                  borderRadius: 4,
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.5)',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 20px 50px rgba(102, 126, 234, 0.15)',
                    '& .icon-box': {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                    }
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box 
                    className="icon-box"
                    sx={{ 
                      width: 64, height: 64, 
                      borderRadius: 4, 
                      bgcolor: alpha('#667eea', 0.1), 
                      color: '#667eea', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mx: 'auto', mb: 3,
                      transition: 'all 0.4s ease'
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Booking Form */}
        <Box sx={{ maxWidth: 850, mx: 'auto', mb: 8 }}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 1 }}>
              Start Planning
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Provide us with a few details, and our corporate team will draft the perfect proposal.
            </Typography>
          </Box>
          
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 6 }, 
              borderRadius: 6,
              border: '1px solid rgba(0,0,0,0.05)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.04)',
              bgcolor: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Subtle background decoration */}
            <Box 
              sx={{ 
                position: 'absolute', top: -50, right: -50, width: 150, height: 150, 
                borderRadius: '50%', background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
                filter: 'blur(30px)', pointerEvents: 'none'
              }} 
            />

            <Stepper 
              activeStep={activeStep} 
              sx={{ 
                mb: 6,
                '& .MuiStepIcon-root.Mui-active': { color: '#667eea' },
                '& .MuiStepIcon-root.Mui-completed': { color: '#10b981' },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel 
                    StepIconProps={{
                      sx: { 
                        '&.Mui-active': { color: '#667eea', filter: 'drop-shadow(0 2px 8px rgba(102,126,234,0.4))' },
                        '&.Mui-completed': { color: '#10b981' }
                      }
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>
                {error}
              </Alert>
            )}

            <Fade in={true} key={activeStep}>
              <Box>
                {activeStep === 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Company Name" name="companyName" value={formData.companyName} onChange={handleChange} required variant="outlined" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Contact Person" name="contactPerson" value={formData.contactPerson} onChange={handleChange} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Designation" name="designation" value={formData.designation} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Phone Number" name="phone" value={formData.phone} onChange={handleChange} required />
                    </Grid>
                  </Grid>
                )}

                {activeStep === 1 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth select label="Event Type" name="eventType" value={formData.eventType} onChange={handleChange} required>
                        {eventTypes.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Estimated Attendees" name="numberOfAttendees" type="number" value={formData.numberOfAttendees} onChange={handleChange} required InputProps={{ inputProps: { min: 10 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Expected Event Date" name="eventDate" type="date" value={formData.eventDate} onChange={handleChange} required InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Venue Preference" name="venuePreference" value={formData.venuePreference} onChange={handleChange} placeholder="e.g., Goa, Dubai, Bali" />
                    </Grid>
                  </Grid>
                )}

                {activeStep === 2 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Budget Range (INR)" name="budget" value={formData.budget} onChange={handleChange} required placeholder="e.g., 5,00,000 - 10,00,000" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Preferred Duration" name="duration" value={formData.duration} onChange={handleChange} placeholder="e.g., 3 Days / 2 Nights" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline rows={4} label="Additional Requirements" name="requirements" value={formData.requirements} onChange={handleChange} placeholder="Please specify any particular team-building activities, dietary constraints, or conference setup needs..." />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Fade>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5, pt: 3, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
                sx={{ fontWeight: 700, px: 3, borderRadius: 50, color: '#0C2340', borderColor: '#0C2340', borderWidth: 2, '&:hover': { bgcolor: 'rgba(12,35,64,0.04)', borderColor: '#0C2340' } }}
              >
                Back
              </Button>
              <Box>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !validateStep(activeStep)}
                    endIcon={!loading && <CheckCircleOutlineIcon />}
                    sx={{ 
                      px: 4, py: 1.2, borderRadius: 50, fontWeight: 700, textTransform: 'none',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
                      '&:hover': { boxShadow: '0 12px 25px rgba(16, 185, 129, 0.4)' }
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Request'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!validateStep(activeStep)}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      px: 4, py: 1.2, borderRadius: 50, fontWeight: 700, textTransform: 'none',
                      background: 'linear-gradient(135deg, #0C2340 0%, #183B63 100%)',
                      boxShadow: '0 8px 20px rgba(12,35,64,0.3)',
                      '&:hover': { boxShadow: '0 12px 25px rgba(12,35,64,0.4)' }
                    }}
                  >
                    Next Step
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default CorporateBooking;